/**
 * Lightweight analytics utility for rmultiverse.com
 *
 * - Fire-and-forget: never throws, never blocks UI
 * - Session ID: generated once per browser tab, stored in sessionStorage
 * - UTM params: captured from URL on first load, stored in sessionStorage
 * - All calls go to our own backend — no third-party tracking
 */

const ENDPOINT = '/api/app-analytics/event';

// ─── Session ──────────────────────────────────────────────────────────────────

function getSessionId() {
  let sid = sessionStorage.getItem('_rmv_sid');
  if (!sid) {
    sid = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : generateFallbackId();
    sessionStorage.setItem('_rmv_sid', sid);
  }
  return sid;
}

function generateFallbackId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ─── UTM capture ─────────────────────────────────────────────────────────────

function captureUtm() {
  // Persist UTMs for the whole session — attribution stays even after navigation
  const cached = sessionStorage.getItem('_rmv_utm');
  if (cached) return JSON.parse(cached);

  const params = new URLSearchParams(window.location.search);
  const utm = {
    utm_source:   params.get('utm_source')   || null,
    utm_medium:   params.get('utm_medium')   || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_content:  params.get('utm_content')  || null,
    utm_term:     params.get('utm_term')     || null,
  };

  // Only persist if there's at least one UTM value
  if (utm.utm_source || utm.utm_medium || utm.utm_campaign) {
    sessionStorage.setItem('_rmv_utm', JSON.stringify(utm));
  }

  return utm;
}

// ─── Core send ───────────────────────────────────────────────────────────────

async function send(payload) {
  try {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    await fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      // keepalive lets the request complete even if the user navigates away
      keepalive: true,
    });
  } catch {
    // Never propagate analytics errors to the user
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Track a page view. Call this on every route change.
 * @param {string} page - The current pathname e.g. '/journal'
 */
export function trackPageView(page) {
  const utm = captureUtm();
  send({
    session_id: getSessionId(),
    event_name: 'page_view',
    page: page || window.location.pathname,
    referrer: document.referrer || null,
    ...utm,
  });
}

/**
 * Track a custom event.
 * @param {string} name - Event name, e.g. 'broker_connected'
 * @param {Object} [properties] - Optional key/value metadata
 */
export function trackEvent(name, properties) {
  const utm = captureUtm();
  send({
    session_id: getSessionId(),
    event_name: name,
    page: window.location.pathname,
    properties: properties || null,
    ...utm,
  });
}
